/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../app/nls/StringResources',
	'../../../dashboard/layout/LayoutHelper',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/ui/interaction/Utils',
	'../../../lib/@waca/baglass/js/baglass/utils/Utils',
	'../../../lib/@waca/core-client/js/core-client/ui/dialogs/ConfirmationDialog',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'underscore',
	'../../../dashboard/widgets/PropertyListUtil',
	'../../../dashboard/util/ContentRegistryUtil'
], (StringResources, LayoutHelper, APIFactory, CommonUtils, Utils, ConfirmationDialog, PropertiesProviderAPI, _, PropertyListUtil, ContentRegistryUtil) => {
	const WIDGET_SIZES = {
		'widget.text': {
			min:{
				width: 100,
				height: 40
			}
		},
		default: {
			min:{
				width: 25,
				height: 25
			}
		}
	};

	class LayoutProperties {
		constructor(options) {
			this.dashboard = options.dashboardApi || options.dashboardAPI;
			this._canvas = options.features['Canvas'];
			this.content = options.content;
			this.content.getFeature('Properties').registerProvider(this.getAPI());
		}

		getLayoutModel() {
			const boardModel = this.dashboard.getFeature('internal').getBoardModel();
			return boardModel.layout.findModel(this.content.getId());
		}

		getAPI() {
			return APIFactory.createAPI(this, [PropertiesProviderAPI]);
		}

		_getCommonType(contentType) {
			return contentType && contentType.split('.')[0];
		}

		_getSubType(contentType) {
			if (contentType && contentType.indexOf('.') !== -1) {
				return contentType.split('.')[1];
			} else {
				return null;
			}
		}

		getPropertyList() {
			const propertyList = [];
			const contentType = this.content.getType();
			const commonType = this._getCommonType(contentType);
			const subType = this._getSubType(contentType);

			if (commonType === 'widget' || commonType === 'group' || this._isTypeRegistered(commonType)) {
				propertyList.push(...this._getWidgetPropertyList(subType));
			}

			// Default the setPropertyValue and getPropertyValue for any property that doesn't define those methods
			//propertyList.forEach((property) => {
			if( propertyList ) {
				for(let i=0; i< propertyList.length; i++) {
					let property = propertyList[i];
					if (!property.setPropertyValue) {
						property.setPropertyValue = (value) => this._setPropertyValueInModel(property.id, value);
					}

					if (!property.getPropertyValue) {
						property.getPropertyValue = () => this._getPropertyValueFromModel(property.id);
					}
				}
			}
			//});

			return propertyList;
		}

		_isTypeRegistered(type) {
			const contentTypeRegistry = this.dashboard.getFeature('ContentTypeRegistry');
			return contentTypeRegistry.isTypeRegistered(type);
		}

		getPropertyLayoutList() {
			const propertiesLayoutSpec = [];

			if (!this._isTopLevelPage()) {
				propertiesLayoutSpec.push({
					'value': StringResources.get('settings'),
					'id': 'banner',
					'type': 'Banner',
					'editable': false,
					'override': false // a flag means if we already have the banner defined by other property provider, then do not override
				});
			}

			const commonType = this._getCommonType(this.content.getType());
			if (this._isTypeRegistered(commonType) || commonType === 'widget' || commonType === 'group') {
				propertiesLayoutSpec.push({
					id: 'general',
					type: 'Group',
					label: StringResources.get('tabName_general')
				}, {
					id: 'appearance',
					type: 'Section',
					open: false,
					label: StringResources.get('sectionName_appearance'),
					position: 1
				}, {
					id: 'layout',
					type: 'Section',
					open: true,
					label: StringResources.get('sectionName_layout'),
					position: 2
				}, {
					id: 'widgetPosition',
					type: 'Section',
					collapsible: false,
					label: StringResources.get('propPositionLabel'),
					position: 2
				}, {
					id: 'moveWidgetSplit',
					type: 'Split',
					items: [{
						id: 'left',
						align: 'left',
						items: []
					}, {
						id: 'right',
						align: 'right',
						items: []
					}]
				}, {
					id: 'resizeWidget',
					type: 'Section',
					collapsible: false,
					label: StringResources.get('propResizeLabel'),
					position: 3
				}, {
					id: 'rotate',
					type: 'Section',
					collapsible: false,
					label: StringResources.get('propRotateLabel'),
					position: 4
				}, {
					id: 'resizeWidgetSplit',
					type: 'Split',
					items: [{
						id: 'left',
						align: 'left',
						items: []
					}, {
						id: 'right',
						align: 'right',
						items: []
					}]
				});
			}

			return propertiesLayoutSpec;
		}

		getUnit() {
			return this._canvas.getPropertyValue('layoutPositioning') === 'absolute' ? 'px' : '%';
		}

		_getMaxValueForConstraint(constrainedBy) {
			if (this._canvas.getPropertyValue('layoutPositioning') === 'relative') {
				return 100;
			} else {
				if (constrainedBy === 'width' || constrainedBy === 'left') {
					return parseFloat(this._canvas.getPropertyValue('pageSizeWidth'));
				} else {
					return parseFloat(this._canvas.getPropertyValue('pageSizeHeight'));
				}
			}
		}

		_getNumericValueWithConstraint(constrainedBy, propertyValue, minimum) {
			let numericValue = parseFloat(propertyValue);
			if (isNaN(numericValue) ) {
				numericValue = 0;
			}
			let constraint = this.content.getPropertyValue(constrainedBy) || 0;
			const maxValue = this._getMaxValueForConstraint(constrainedBy);
			numericValue = Math.max(Math.min(maxValue, numericValue), minimum || 0);
			constraint = parseFloat(constraint);
			if (constraint + numericValue > maxValue) {
				numericValue = maxValue - constraint;
			}

			return numericValue;
		}

		_onChangePercentOrPixelProperty(constrainedBy, propertyName, propertyValue, options) {
			const units = this.getUnit();
			let minimum =  WIDGET_SIZES[this.content.getType()] ? WIDGET_SIZES[this.content.getType()].min[propertyName] : WIDGET_SIZES.default.min[propertyName];
			if ( minimum && units === '%'){
				// get the minimum value to percent
				const domFeature = this.content.getContainer().getFeature('ContentViewDOM');
				if (domFeature){
					const node = domFeature.getNode();
					const size = propertyName === 'height' ?  node.clientHeight : node.clientWidth;
					minimum = 100 * minimum / size;
				} else {
					// without a dom we can't figure out the minimum percent.. so we use 0
					minimum = 0;
				}
			}

			let numericValue = this._getNumericValueWithConstraint(constrainedBy, propertyValue, minimum);

			let formattedValue = PropertyListUtil.getPropertyDisplayString(numericValue, units);

			this.content.setPropertyValue(propertyName, formattedValue, options);
		}

		_onChangeAngle(propertyName, propertyValue) {
			this.content.setPropertyValue(propertyName, propertyValue);
		}

		_getWidgetPropertyList() {
			const model = this.getLayoutModel();
			let isInGroup = false;
			const selection = this._canvas.getSelectedContentList();
			if (selection && selection.length) {
				const selectionParent = selection[0].getContainer();
				if (selectionParent) {
					isInGroup = selectionParent.getType() === 'group';
				}
			}

			let propertyList = [{
				getPropertyValue: () => {
					if (model.style && (model.style.opacity || model.style.opacity === 0)) {
						return model.style.opacity;
					} else {
						return 1;
					}
				},
				setPropertyValue: (value, options) => {
					this._updateStyleValueInModel('opacity', value, options);
				},
				id: 'opacity',
				editor: {
					sectionId: 'general.appearance',
					position: 4,
					uiControl: {
						label: StringResources.get('propOpacity'),
						description: StringResources.get('propOpacityDescription'),
						module: '../ui/UiSlider',
						sliderType: 'percentage',
						connect: [true, false],
						noInputView: true,
						step: 1,
						start: [100],
						startLabels: [StringResources.get('propOpacityDescription')],
						range: {
							'min': 0,
							'max': 100
						},
						format: {
							'decimals': 0,
							'postfix': '%'
						}
					}
				}
			}];
			let leftProperty = {
				id: 'left',
				getPropertyValue: () => {
					if (model.style) {
						return model.style.left;
					}
				},
				setPropertyValue: (value, options) => {
					this._updateStyleValueInModel('left', value, options);
				},
				editor: {
					sectionId: 'general.layout.widgetPosition.moveWidgetSplit.left',
					uiControl: {
						type: 'InputLabel',
						label: StringResources.get('propPositionXAxis'),
						multiline: true,
						handleReturnKey: true,
						decimalPlaces: 2,
						onChange: this._onChangePercentOrPixelProperty.bind(this, 'width')
					}
				},
				onPropertyChange: {
					refresh: {
						propertiesPane: true
					}
				}
			};
			if (isInGroup) {
				delete leftProperty.editor;
			}
			propertyList.push(leftProperty);
			let topProperty = {
				id: 'top',
				getPropertyValue: () => {
					if (model.style) {
						return model.style.top;
					}
				},
				setPropertyValue: (value, options) => {
					this._updateStyleValueInModel('top', value, options);
				},
				editor: {
					sectionId: 'general.layout.widgetPosition.moveWidgetSplit.right',
					uiControl: {
						type: 'InputLabel',
						label: StringResources.get('propPositionYAxis'),
						multiline: true,
						handleReturnKey: true,
						decimalPlaces: 2,
						onChange: this._onChangePercentOrPixelProperty.bind(this, 'height')
					}
				},
				onPropertyChange: {
					refresh: {
						propertiesPane: true
					}
				}
			};
			if (isInGroup) {
				delete topProperty.editor;
			}
			propertyList.push(topProperty);
			let widthProperty = {
				id: 'width',
				getPropertyValue: () => {
					if (model.style) {
						return model.style.width;
					}
				},
				setPropertyValue: (value, options) => {
					this._updateStyleValueInModel('width', value, options);
				},
				editor: {
					sectionId: 'general.layout.resizeWidget.resizeWidgetSplit.left',
					uiControl: {
						type: 'InputLabel',
						label: StringResources.get('propResizeWidth'),
						multiline: true,
						handleReturnKey: true,
						decimalPlaces: 2,
						onChange: this._onChangePercentOrPixelProperty.bind(this, 'left')
					}
				},
				onPropertyChange: {
					refresh: {
						propertiesPane: true
					}
				}
			};
			if (isInGroup) {
				delete widthProperty.editor;
			}
			propertyList.push(widthProperty);
			let heightProperty = {
				id: 'height',
				getPropertyValue: () => {
					if (model.style) {
						return model.style.height;
					}
				},
				setPropertyValue: (value, options) => {
					this._updateStyleValueInModel('height', value, options);
				},
				editor: {
					sectionId: 'general.layout.resizeWidget.resizeWidgetSplit.right',
					uiControl: {
						type: 'InputLabel',
						label: StringResources.get('propResizeHeight'),
						multiline: true,
						handleReturnKey: true,
						decimalPlaces: 2,
						onChange: this._onChangePercentOrPixelProperty.bind(this, 'top')
					}
				},
				onPropertyChange: {
					refresh: {
						propertiesPane: true
					}
				}
			};
			if (isInGroup) {
				delete heightProperty.editor;
			}
			propertyList.push(heightProperty);

			const rotationSupported = this._isCapabilityEnabled('rotation');

			propertyList.push({
				id: 'canRotate',
				getPropertyValue: () => rotationSupported,
				setPropertyValue: () => { throw new Error('not supported'); }
			});

			if (rotationSupported) {
				const rotationProperty = {
					id: 'rotateAngle',
					getPropertyValue: () => {
						const transform = model.style && model.style.transform;
						if (transform) {
							const deg = /-?\d*\.?\d*deg/.exec(transform);
							if (deg) {
								const str = deg[0];
								const value = str.substr(0, str.length - 3);
								return parseFloat(value) || 0;
							} else {
								return CommonUtils.getAngleFromMatrix(transform);
							}
						} else {
							return 0;
						}
					},
					setPropertyValue: (value, options) => {
						value = parseFloat(value);
						const sanitizedValue = ((value % 360) + 360) % 360;
						if (!isNaN(sanitizedValue)) {
							this._updateStyleValueInModel('-webkit-transform', 'rotate( ' + sanitizedValue + 'deg )', options);
							this._updateStyleValueInModel('transform', 'rotate( ' + sanitizedValue + 'deg )', options);
						}
					},
					editor: {
						sectionId: 'general.layout.rotate',
						uiControl: {
							type: 'InputLabel',
							label: StringResources.get('propRotateAngle'),
							handleReturnKey: true,
							onChange: this._onChangeAngle.bind(this)
						}
					}
				};
				if (isInGroup) {
					delete rotationProperty.editor;
				}
				propertyList.push(rotationProperty);
			}

			return propertyList;
		}

		_isCapabilityEnabled(capability) {
			const capabilitiesFeature = ContentRegistryUtil.getCapabilities(this.content);
			return this.content.getType() !== 'widget.live' && (capabilitiesFeature ? capabilitiesFeature.getCapabilities()[capability] : true);
		}

		_getPropertyValueFromModel(propName) {
			const model = this.getLayoutModel();
			// In case of livewidget previews, we have a content but it is not attached to the layout, which causes the model to be null.
			return model && model.get(propName);
		}

		_setPropertyValueInModel(propName, propValue, options) {
			const payload = {};
			const model = this.getLayoutModel();
			payload[propName] = propValue;
			model.set(payload, options);
		}

		_updateStyleValueInModel(styleName, value) {
			let styleValue = value;
			const model = this.getLayoutModel();
			const newStyle = _.extend({}, model.style);
			newStyle[styleName] = styleValue;

			// Todo: we can stop using the topLayoutModel once the undo/redo uses
			// the API events, but for now using the model directly doesn't add to the stack
			// if the widget is not on the same tab that it was initally added on.
			model.getTopLayoutModel().updateModel({
				updateArray: [{
					id: model.id,
					style: newStyle
				}]
			}, this, {
				skipUndoRedo: true
			});

			return styleValue;
		}

		_isTopLevelPage() {
			return !this.content.getContainer();
		}
	}

	return LayoutProperties;
});
